/**
 * THUB-243: transport-aware guidance for a failed connection test.
 *
 * Previously a single `ERROR_CODE_MESSAGES` map was duplicated verbatim in three components and
 * keyed on `errorCode` alone, with no awareness of the transport. So an Azure Blob failure was
 * told to "Verify credentials or SSH key configuration" -- there is no SSH key in Azure Blob,
 * and in the case that prompted this the credential was fine: the request was refused by the
 * partner's storage firewall (see THUB-242).
 *
 * Two rules follow from that:
 *
 *  1. Never mention SFTP/SSH concepts unless the connection actually is SFTP.
 *  2. Prefer the transport's OWN error code when the backend gives us one. Azure distinguishes
 *     failures that our generic `AUTH_FAILED` flattens together, and they have different
 *     remedies:
 *
 *       AuthenticationFailed            -> signature invalid; re-mint the SAS
 *       AuthorizationFailure            -> signature VALID, request refused; check the storage
 *                                          account's network rules / IP allowlist
 *       AuthorizationPermissionMismatch -> credential lacks the permission for this operation;
 *                                          check the SAS `sp=` permissions
 *
 * The backend embeds the Azure code in the error text as `[AuthorizationFailure]`, so it is
 * extracted here rather than requiring an API change. If a structured field is added later,
 * prefer it and keep this as the fallback.
 */

export type TransferMethod = 'sftp' | 'api' | 'azure_blob' | string | undefined;

/** Generic, transport-neutral wording. Used when nothing more specific applies. */
const GENERIC: Record<string, string> = {
  NETWORK_UNREACHABLE: 'Unable to reach the remote endpoint. Check the address and that it is publicly resolvable.',
  AUTH_FAILED: 'Authentication failed. Verify the stored credential for this connection.',
  DIR_ACCESS_DENIED: 'Access denied to the target location. Check the permissions granted to the credential.',
  SECRET_ACCESS_ERROR: 'Failed to access credentials from AWS Secrets Manager.',
  TIMEOUT: 'Connection timed out. The endpoint may be slow or unreachable.',
  CONNECTOR_NOT_FOUND: 'Transfer Family connector not found. Connection setup may not have completed.',
  RATE_LIMITED: 'Too many test requests. Please wait before testing again.',
  TEST_IN_PROGRESS: 'A test is already in progress for this connection.',
  UNKNOWN_ERROR: 'An unexpected error occurred during the connection test.',

  // THUB-138 split the backend's catch-all classifier into distinct failure modes, so these
  // five codes are now emitted as well. Without wording here they fall through to the raw
  // error and the operator loses the distinction the classifier exists to draw -- above all
  // between "our packets were dropped" (chase the partner's firewall) and "we reached the
  // server and it refused us" (do not).
  CONNECTION_REFUSED:
    'The endpoint actively refused the connection. It is reachable, so the service may be stopped or listening on a different port.',
  DNS_FAILURE:
    'The hostname could not be resolved. Check it for typos; an internal-only name will not resolve from TransferHub.',
  PATH_NOT_FOUND:
    'We connected and authenticated, but the target path does not exist. Confirm the exact path -- paths are case-sensitive and may be relative to the login directory.',
  ENDPOINT_ERROR:
    'The endpoint answered but returned an error. This is not a network or firewall problem -- see the technical details below for what it returned.',
  HOST_KEY_MISMATCH:
    "The server's host key does not match the key we trust. Expected if the partner rebuilt or migrated the server; otherwise verify the fingerprint with them before proceeding.",
};

/** SFTP keeps the original wording -- host/port and SSH keys are real concepts there. */
const SFTP: Record<string, string> = {
  NETWORK_UNREACHABLE: 'Unable to reach the remote server. Check the host and port configuration.',
  AUTH_FAILED: 'Authentication failed. Verify credentials or SSH key configuration.',
  DIR_ACCESS_DENIED: 'Directory access denied. Check permissions on the remote server.',
  // Host keys are SFTP-only, and the remedy is a specific GUI action, so name it here.
  HOST_KEY_MISMATCH:
    "The server's SSH host key does not match the key we trust. If the partner rebuilt or migrated their server, use Fetch Host Key to re-trust it. If they changed nothing, do not proceed -- verify the fingerprint with them first.",
};

/** Azure Blob: SAS-based, container-scoped, commonly behind a storage firewall. */
const AZURE: Record<string, string> = {
  NETWORK_UNREACHABLE: 'Unable to reach the blob endpoint. Check the container URL.',
  AUTH_FAILED: 'Azure rejected the request. This is usually the SAS token or the storage account network rules.',
  DIR_ACCESS_DENIED: 'Azure denied access to the container. Check the permissions on the SAS token.',
};

/** HTTP/API: base URL + auth scheme, no directories. */
const HTTP: Record<string, string> = {
  NETWORK_UNREACHABLE: 'Unable to reach the endpoint. Check the base URL and that it is publicly resolvable.',
  AUTH_FAILED: 'The endpoint rejected our credentials. Verify the auth type and stored secret.',
  DIR_ACCESS_DENIED: 'The endpoint denied access to the resource path.',
};

/**
 * Azure's own error codes, which are more precise than our generic ones. Keyed exactly as Azure
 * returns them in `x-ms-error-code`.
 */
const AZURE_CODES: Record<string, string> = {
  AuthenticationFailed:
    'The SAS token signature is invalid or expired. Re-mint the token and update the connection.',
  AuthorizationFailure:
    'The SAS token is valid but Azure refused the request — this is typically the storage account firewall. Ask the partner to allowlist our egress IP addresses (shown on this connection).',
  AuthorizationPermissionMismatch:
    'The SAS token authenticated but lacks the permission for this operation. Ask the partner for a token with the required permissions (read/write/list).',
  ContainerNotFound: 'The container does not exist at that URL. Check the container name.',
  AccountIsDisabled: 'The Azure storage account is disabled. The partner needs to re-enable it.',
  InvalidResourceName: 'The container URL contains an invalid resource name.',
};

/** Extract an Azure error code the backend embedded as `[Code]` in the error text. */
export function extractAzureErrorCode(errorText?: string | null): string | undefined {
  if (!errorText) return undefined;
  const match = errorText.match(/\[([A-Za-z]+)\]/);
  const code = match?.[1];
  return code && code in AZURE_CODES ? code : undefined;
}

function tableFor(transferMethod: TransferMethod): Record<string, string> {
  if (transferMethod === 'sftp') return { ...GENERIC, ...SFTP };
  if (transferMethod === 'azure_blob') return { ...GENERIC, ...AZURE };
  if (transferMethod === 'api') return { ...GENERIC, ...HTTP };
  return GENERIC;
}

/**
 * Guidance for a failed test. Prefers the transport's specific error code, then a
 * transport-appropriate message for our generic code, then the raw backend error.
 *
 * @param errorCode  our ConnectionTestErrorCode (e.g. AUTH_FAILED)
 * @param errorText  the backend's error string; may embed an Azure `[Code]`
 * @param transferMethod  sftp | azure_blob | api
 */
export function getConnectionTestGuidance(
  errorCode?: string | null,
  errorText?: string | null,
  transferMethod?: TransferMethod,
): string | undefined {
  // Azure's own code is the most precise signal available -- prefer it.
  if (transferMethod === 'azure_blob') {
    const azureCode = extractAzureErrorCode(errorText);
    if (azureCode) return AZURE_CODES[azureCode];
  }
  if (errorCode) {
    const table = tableFor(transferMethod);
    if (table[errorCode]) return table[errorCode];
  }
  return errorText ?? undefined;
}

/** Exported for tests and for anything that needs the raw tables. */
const __tables = { GENERIC, SFTP, AZURE, HTTP, AZURE_CODES };
